import { TextField } from '@material-ui/core';
import React, { FocusEventHandler, ReactNode, useCallback } from 'react';
import { useIntl } from 'react-intl';
import NumberFormat from 'react-number-format';
import { useSelector } from 'react-redux';

import ReadonlyItem from '@/components/input/ReadonlyItem';
import MoneyAmountValue from '@/components/output/MoneyAmountValue';
import { MoneyType, Value } from '@/model/common';
import { makeSelectLocale } from '@/model/global/selectors';
import { Func } from '@/utils/ts';

const selectLocale = makeSelectLocale();
const getSeparator = (locale: string, separatorType: Intl.NumberFormatPartTypes): string | undefined => {
  const numberWithGroupAndDecimalSeparator = 10000.1;
  return Intl.NumberFormat(locale)
    ?.formatToParts(numberWithGroupAndDecimalSeparator)
    ?.find((part) => part.type === separatorType)?.value;
};

interface MoneyMaskInputProps {
  inputRef?: (ref: HTMLElement | null) => void;
  onFocus?: FocusEventHandler<HTMLInputElement>;
}

const MoneyMaskInput: React.FC<MoneyMaskInputProps> = ({ onFocus, inputRef, ...other }: MoneyMaskInputProps) => {
  const locale = useSelector(selectLocale);
  return (
    <NumberFormat
      {...other}
      onFocus={(event): void => {
        onFocus?.(event);
        setTimeout(() => event.target.select(), 0);
      }}
      fixedDecimalScale
      allowNegative={false}
      decimalScale={2}
      decimalSeparator={getSeparator(locale, 'decimal')}
      thousandSeparator={getSeparator(locale, 'group')}
      inputMode="numeric"
    />
  );
};

export interface MoneyAmountItemProps {
  'data-test'?: string;
  value?: Value<MoneyType>;
  onChange?: Func<[MoneyType | undefined]>;
  readonly?: boolean;
  messages?: {
    label?: string;
    error?: ReactNode;
  };
}

const MoneyAmountItem: React.FC<MoneyAmountItemProps> = ({
  value,
  readonly,
  onChange,
  messages,
  'data-test': dataTest,
}) => {
  const { formatMessage } = useIntl();
  const handleChange = useCallback(
    (maskedValue: string): void => {
      if (!onChange) {
        return;
      }
      const newValue = maskedValue.replace(/[^\d]/g, ''); // exclude all non digits
      const parsedValue = newValue ? parseFloat(newValue) : 0;
      if (parsedValue !== value?.value) {
        onChange(parsedValue);
      }
    },
    [onChange, value],
  );
  return readonly ? (
    <ReadonlyItem data-test={dataTest} label={messages?.label}>
      <MoneyAmountValue value={value?.value} withCurrency fraction={2} data-test={dataTest && `${dataTest}-value`} />
    </ReadonlyItem>
  ) : (
    <TextField
      variant="filled"
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      InputLabelProps={{ 'data-test': dataTest && `${dataTest}-label`, shrink: true }}
      InputProps={{
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
        // @ts-ignore
        'data-test': dataTest && `${dataTest}-input`,
        className: 'ym-hide-content',
        disableUnderline: true,
        inputComponent: MoneyMaskInput,
      }}
      label={`${messages?.label}, ${formatMessage({ id: 'common.components.money.currency' })}`}
      value={value?.value ? value?.value / 100 : 0}
      onChange={async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        if (!onChange) {
          return;
        }
        const inputValue = event.target.value;
        await handleChange(inputValue);
      }}
      {...(value?.hasError
        ? {
            helperText: messages?.error,
            error: true,
          }
        : {})}
      // FIXME: best place to trim, but onBlur handler stopping event propagation, so i.e submit does't work at all
      // onBlur={async (event: React.FocusEvent<HTMLInputElement>): Promise<void> => {
      //   const inputValue = event.target.value?.trim();
      //   await onChange(inputValue);
      // }}
    />
  );
};

export default MoneyAmountItem;
